import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { dealApi, dealQueryKeys } from "service/crm/api/deal";
import { GetDealOptionsParams } from "types/crm/deal";
import {
  restoreDealOptions,
  setDealOptionAssociated,
} from "./dealOptionsCache";
import { companyQueryKeys } from "../api/company";

interface UseQueryOptions {
  enabled?: boolean;
}

/** Deals associated with a company — GET /crm/companies/{companyUuid}/deals */
export const useCompanyDeals = (
  companyUuid: string,
  options: UseQueryOptions = {},
) => {
  return useQuery({
    queryKey: dealQueryKeys.byCompany(companyUuid),
    queryFn: () => dealApi.getDealsByCompany(companyUuid),
    enabled: (options.enabled ?? true) && !!companyUuid,
  });
};

/** Deals the company can be linked to, each flagged with `associated`. */
export const useCompanyDealOptions = (
  companyUuid: string,
  params: Omit<GetDealOptionsParams, "page"> = {},
  options: UseQueryOptions = {},
) => {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: dealQueryKeys.companyOptions(companyUuid, params),
    queryFn: ({ pageParam }) =>
      dealApi.getCompanyDealOptions(companyUuid, { ...params, page: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.last ? undefined : lastPage.page + 1,
    enabled: (options.enabled ?? true) && !!companyUuid,
  });

  return {
    deals: data?.pages.flatMap((p) => p.content) ?? [],
    isLoading,
    isFetchingMore: isFetchingNextPage,
    hasMore: !!hasNextPage,
    onLoadMore: fetchNextPage,
    totalElements: data?.pages[0]?.totalElements ?? 0,
  };
};

export const useCreateCompanyDealMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: dealApi.createDealForCompany,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: dealQueryKeys.all });
      queryClient.invalidateQueries({
        queryKey: dealQueryKeys.byCompany(variables.companyUuid),
      });
      queryClient.invalidateQueries({
        queryKey: companyQueryKeys.activities(variables.companyUuid),
      });
    },
  });
};

export const useAssociateCompanyDealMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: dealApi.associateDealWithCompany,
    onMutate: async (variables) => {
      const scopeKey = dealQueryKeys.byCompany(variables.companyUuid);
      await queryClient.cancelQueries({ queryKey: scopeKey });
      return {
        snapshot: setDealOptionAssociated(
          queryClient,
          scopeKey,
          variables.dealUuid,
          true,
        ),
      };
    },
    onError: (_error, _variables, context) =>
      restoreDealOptions(queryClient, context?.snapshot),
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: dealQueryKeys.byCompany(variables.companyUuid),
      });
      queryClient.invalidateQueries({
        queryKey: companyQueryKeys.activities(variables.companyUuid),
      });
    },
  });
};

export const useDisassociateCompanyDealMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: dealApi.disassociateDealFromCompany,
    onMutate: async (variables) => {
      const scopeKey = dealQueryKeys.byCompany(variables.companyUuid);
      await queryClient.cancelQueries({ queryKey: scopeKey });
      return {
        snapshot: setDealOptionAssociated(
          queryClient,
          scopeKey,
          variables.dealUuid,
          false,
        ),
      };
    },
    onError: (_error, _variables, context) =>
      restoreDealOptions(queryClient, context?.snapshot),
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: dealQueryKeys.byCompany(variables.companyUuid),
      });
      queryClient.invalidateQueries({
        queryKey: companyQueryKeys.activities(variables.companyUuid),
      });
    },
  });
};
